import CircleFilled from "@/assets/icons/CircleFilled";
import Lock from "@/assets/icons/Lock";
import { FormControl } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Radio, RadioGroup, RadioIcon, RadioIndicator, RadioLabel } from "@/components/ui/radio";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import type { RateMini } from "ipctg-inflow-types/InFlowAPIMisc";
import { memo, useState } from "react";
import { Pressable } from "react-native";

interface RateSelectionProps {
  rates: RateMini[] | undefined;

  onRateSelected: (rate: RateMini, rateName: string) => void;

  selectedRate?: RateMini["rateID"];
}

const RateItem = memo(
  ({
    rate,
    isSelected,
    onSelect,
  }: {
    rate: RateMini;
    isSelected: boolean;
    onSelect: () => void;
  }) => {
    return (
      <Pressable onPress={onSelect}>
        <Radio
          value={rate.rateID}
          className={`h-[64px] p-[20px] gap-[12px] rounded-[6px] border-[1px] ${
            isSelected ? "bg-primary-200 border-primary-800" : "bg-background-100 border-outline-300"
          }`}
        >
          <HStack className="w-full justify-between items-center">
            <HStack space="md" className="items-center">
              <RadioIndicator className="text-primary-800 h-6 w-6 ">
                <RadioIcon as={CircleFilled} className="text-primary-800 h-4 w-4" />
              </RadioIndicator>
              <VStack>
                <HStack space="sm" className="items-center">
                  <RadioLabel className="font-[600] text-typography-900 text-[16px] leading-[22px] align-middle">{rate.label}</RadioLabel>
                  {rate.requiresPassphrase && <Lock />}
                </HStack>
              </VStack>
            </HStack>
            <Text className="text-[16px] font-[700] leading-[22px] align-middle text-typography-900">${rate.rateAmount}</Text>
          </HStack>
        </Radio>
      </Pressable>
    );
  },
);

const RateSelection: React.FC<RateSelectionProps> = ({ rates, onRateSelected, selectedRate }) => {
  const [selected, setSelected] = useState(selectedRate);

  const handleChange = (value: string) => {
    setSelected(value);
    const selectedRate = rates?.find((rate) => rate.rateID === value);
    if (selectedRate) {
      onRateSelected(selectedRate, selectedRate.label);
    }
  };

  return (
    <FormControl key="rate-selection-input">
      <RadioGroup value={selected} onChange={(val: string) => handleChange(val)}>
        <VStack space="md" className="gap-[8px]">
          {rates?.map((rate) => (
            <RateItem key={rate.rateID} rate={rate} isSelected={selected === rate.rateID} onSelect={() => handleChange(rate.rateID)} />
          ))}
        </VStack>
      </RadioGroup>
    </FormControl>
  );
};

export default RateSelection;
